import { PERMISSIONS } from '../config/permissions';
export const canViewRoles = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  if (!userPermissions) return false;
  
  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(PERMISSIONS.ACTIONS.ROLE_VIEW);
  }
  
  const modulePermissions = userPermissions[PERMISSIONS.MODULES.ROLES_PERMISSION];
  if (modulePermissions && Array.isArray(modulePermissions)) {
    return modulePermissions.includes(PERMISSIONS.ACTIONS.ROLE_VIEW);
  }
  
  return false;
};

export const canCreateRoles = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  if (!userPermissions) return false;
  
  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(PERMISSIONS.ACTIONS.ROLE_CREATE);
  }
  
  const modulePermissions = userPermissions[PERMISSIONS.MODULES.ROLES_PERMISSION];
  if (modulePermissions && Array.isArray(modulePermissions)) {
    return modulePermissions.includes(PERMISSIONS.ACTIONS.ROLE_CREATE);
  }
  
  return false;
};

export const canEditRoles = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  if (!userPermissions) return false;
  
  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(PERMISSIONS.ACTIONS.ROLE_EDIT);
  }
  
  const modulePermissions = userPermissions[PERMISSIONS.MODULES.ROLES_PERMISSION];
  if (modulePermissions && Array.isArray(modulePermissions)) {
    return modulePermissions.includes(PERMISSIONS.ACTIONS.ROLE_EDIT);
  }
  
  return false;
};

/**
 * Check if user can delete roles
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can delete roles
 */
export const canDeleteRoles = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  if (!userPermissions) return false;
  
  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(PERMISSIONS.ACTIONS.ROLE_DELETE);
  }
  
  const modulePermissions = userPermissions[PERMISSIONS.MODULES.ROLES_PERMISSION];
  if (modulePermissions && Array.isArray(modulePermissions)) {
    return modulePermissions.includes(PERMISSIONS.ACTIONS.ROLE_DELETE);
  }
  
  return false;
};

/**
 * Check if user has any role permission
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user has any role permission
 */
export const hasAnyRolePermission = (userPermissions, isMainVendor) => {
  return canViewRoles(userPermissions, isMainVendor) ||
         canCreateRoles(userPermissions, isMainVendor) ||
         canEditRoles(userPermissions, isMainVendor) ||
         canDeleteRoles(userPermissions, isMainVendor);
};

/**
 * Check if user has all role permissions
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user has all role permissions
 */
export const hasAllRolePermissions = (userPermissions, isMainVendor) => {
  return canViewRoles(userPermissions, isMainVendor) &&
         canCreateRoles(userPermissions, isMainVendor) &&
         canEditRoles(userPermissions, isMainVendor) &&
         canDeleteRoles(userPermissions, isMainVendor);
};

/**
 * Get user's role permissions summary
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {Object} - Object with permission status for each role action
 */
export const getRolePermissionsSummary = (userPermissions, isMainVendor) => {
  return {
    canView: canViewRoles(userPermissions, isMainVendor),
    canCreate: canCreateRoles(userPermissions, isMainVendor),
    canEdit: canEditRoles(userPermissions, isMainVendor),
    canDelete: canDeleteRoles(userPermissions, isMainVendor),
    hasAny: hasAnyRolePermission(userPermissions, isMainVendor),
    hasAll: hasAllRolePermissions(userPermissions, isMainVendor),
    isMainVendor
  };
};

/**
 * Get permission level description
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {string} - Description of user's permission level
 */
export const getRolePermissionLevel = (userPermissions, isMainVendor) => {
  if (isMainVendor) return 'Full Access (Main Vendor)';
  
  const summary = getRolePermissionsSummary(userPermissions, isMainVendor);
  
  if (summary.hasAll) return 'Full Access';
  if (summary.canView && summary.canCreate && summary.canEdit) return 'Content Manager';
  if (summary.canView && summary.canCreate) return 'Creator';
  if (summary.canView && summary.canEdit) return 'Editor';
  if (summary.canView) return 'Viewer';
  
  return 'No Access';
};

/**
 * Check if user can access roles management page
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can access roles management
 */
export const canAccessRolesManagement = (userPermissions, isMainVendor) => {
  return canViewRoles(userPermissions, isMainVendor);
};

/**
 * Check if user can view team members
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can view team members
 */
export const canViewTeamMembers = (userPermissions, isMainVendor) => {
  return canViewRoles(userPermissions, isMainVendor);
};

/**
 * Check if user can create team members
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can create team members
 */
export const canCreateTeamMembers = (userPermissions, isMainVendor) => {
  return canCreateRoles(userPermissions, isMainVendor);
};

/**
 * Check if user can edit team members
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can edit team members
 */
export const canEditTeamMembers = (userPermissions, isMainVendor) => {
  return canEditRoles(userPermissions, isMainVendor);
};

/**
 * Check if user can delete team members
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can delete team members
 */
export const canDeleteTeamMembers = (userPermissions, isMainVendor) => {
  return canDeleteRoles(userPermissions, isMainVendor);
};

/**
 * Check if user can manage team members (legacy function)
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can manage team members
 */
export const canManageTeamMembers = (userPermissions, isMainVendor) => {
  return canCreateTeamMembers(userPermissions, isMainVendor) || 
         canEditTeamMembers(userPermissions, isMainVendor) || 
         canDeleteTeamMembers(userPermissions, isMainVendor);
};

/**
 * Get required permissions for specific role actions
 * @param {string} action - The action to check
 * @returns {Object} - Object with module and action
 */
export const getRequiredPermissions = (action) => {
  const permissions = {
    view: {
      module: PERMISSIONS.MODULES.ROLES_PERMISSION,
      action: PERMISSIONS.ACTIONS.ROLE_VIEW
    },
    create: {
      module: PERMISSIONS.MODULES.ROLES_PERMISSION,
      action: PERMISSIONS.ACTIONS.ROLE_CREATE
    },
    edit: {
      module: PERMISSIONS.MODULES.ROLES_PERMISSION,
      action: PERMISSIONS.ACTIONS.ROLE_EDIT
    },
    delete: {
      module: PERMISSIONS.MODULES.ROLES_PERMISSION,
      action: PERMISSIONS.ACTIONS.ROLE_DELETE
    }
  };
  
  return permissions[action] || null;
};

/**
 * Validate role permissions for a specific action
 * @param {string} action - The action to validate
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {Object} - Object with isValid and message
 */
export const validateRolePermission = (action, userPermissions, isMainVendor) => {
  const required = getRequiredPermissions(action);
  
  if (!required) {
    return {
      isValid: false,
      message: 'Invalid action specified'
    };
  }
  
  const hasPermission = (() => {
    if (isMainVendor) return true;
    
    if (!userPermissions) return false;
    
    if (Array.isArray(userPermissions)) {
      return userPermissions.includes(required.action);
    }
    
    const modulePermissions = userPermissions[required.module];
    if (modulePermissions && Array.isArray(modulePermissions)) {
      return modulePermissions.includes(required.action);
    }
    
    return false;
  })();
  
  return {
    isValid: hasPermission,
    message: hasPermission ? 'Permission granted' : `Permission denied: ${required.action} required`,
    required
  };
};

/**
 * Check if user can view partners
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can view partners
 */
export const canViewPartners = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  if (!userPermissions) return false;
  
  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(PERMISSIONS.ACTIONS.VIEW_PARTNERS);
  }
  
  const modulePermissions = userPermissions[PERMISSIONS.MODULES.PARTNER_MANAGEMENT];
  if (modulePermissions && Array.isArray(modulePermissions)) {
    return modulePermissions.includes(PERMISSIONS.ACTIONS.VIEW_PARTNERS);
  }
  
  return false;
};

/**
 * Check if user can create partners
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can create partners
 */
export const canCreatePartners = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  if (!userPermissions) return false;
  
  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(PERMISSIONS.ACTIONS.CREATE_PARTNER);
  }
  
  const modulePermissions = userPermissions[PERMISSIONS.MODULES.PARTNER_MANAGEMENT];
  if (modulePermissions && Array.isArray(modulePermissions)) {
    return modulePermissions.includes(PERMISSIONS.ACTIONS.CREATE_PARTNER);
  }
  
  return false;
};

/**
 * Check if user can edit partners
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can edit partners
 */
export const canEditPartners = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  if (!userPermissions) return false;
  
  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(PERMISSIONS.ACTIONS.EDIT_PARTNER);
  }
  
  const modulePermissions = userPermissions[PERMISSIONS.MODULES.PARTNER_MANAGEMENT];
  if (modulePermissions && Array.isArray(modulePermissions)) {
    return modulePermissions.includes(PERMISSIONS.ACTIONS.EDIT_PARTNER);
  }
  
  return false;
};

/**
 * Check if user can delete partners
 * @param {Object} userPermissions - User's permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean} - True if user can delete partners
 */
export const canDeletePartners = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  if (!userPermissions) return false;
  
  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(PERMISSIONS.ACTIONS.DELETE_PARTNER);
  }
  
  const modulePermissions = userPermissions[PERMISSIONS.MODULES.PARTNER_MANAGEMENT];
  if (modulePermissions && Array.isArray(modulePermissions)) {
    return modulePermissions.includes(PERMISSIONS.ACTIONS.DELETE_PARTNER);
  }
  
  return false;
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  canViewRoles,
  canCreateRoles,
  canEditRoles,
  canDeleteRoles,
  hasAnyRolePermission,
  hasAllRolePermissions,
  getRolePermissionsSummary,
  getRolePermissionLevel,
  canAccessRolesManagement,
  canViewTeamMembers,
  canCreateTeamMembers,
  canEditTeamMembers,
  canDeleteTeamMembers,
  canManageTeamMembers,
  canViewPartners,
  canCreatePartners,
  canEditPartners,
  canDeletePartners,
  getRequiredPermissions,
  validateRolePermission
}; 